import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  InputAdornment,
  Skeleton,
  Table,
  TableContainer,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import { api } from '../../../services/Main/Api'
import {setCashFlow, setCondoId, setCondoName, setCondoType, setRentType, setCondoFeatures, setManagerMode} from '../../../services/Main/AuthStorage'
import Context from "../../../contexts/Context"
import { useNavigate } from "react-router-dom"
import TableCondoHead from "./components/CondoList/TableCondoHead"
import TableCondoBody from "./components/CondoList/TableCondoBody"
import NoResultsAnimation from "../../../components/Shared/animations/NoResultsAnimation"
import PaginationGlobal from "../../../components/Shared/PaginationGlobal"

const useDebounced = (value, delay = 500) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

const CondoList = () => {
  const { setCondoIdLogin } = useContext(Context)
  const navigate = useNavigate()

  const [condoData, setCondoData] = useState(null)
  const [verifier, setVerifier] = useState(null)
  const [loading, setLoading] = useState(false)

  // Filtros da API
  const [filters, setFilters] = useState({
    page: 1,
    page_size: 50,
    status: "ACTIVE",
    search: '' // backend: ajuste para o nome do parâmetro aceito (ex.: 'search', 'name', 'q' etc.)
  })

  // Estado do input de busca
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounced(search, 500)

  // Quando o debounce atualizar, aplicamos no filtro e resetamos a página
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      page: 1,
      search: debouncedSearch || ''
    }))
  }, [debouncedSearch])

  async function fetchData() {
    try {
      setLoading(true)
      const params = { ...filters }
      // Remover chaves vazias para não “sujar” a query
      Object.keys(params).forEach(k => (params[k] === '' || params[k] === null) && delete params[k])

      const res = await api.get(`/condo/`, { params })
      setCondoData(res.data)
    } catch (err) {
      console.error(err)
    } finally {
      setVerifier(true)
      setLoading(false)
    }
  }

  async function changeCondo(value) {
    try {
      const response = await api.get(`/condo/${value}`)
      setCondoId(response.data.id)
      setCondoIdLogin(response.data.id)
      setCondoName(response.data.name)
      setCondoType(response.data.type)
      setRentType(response.data.rent_type)
      setCashFlow(response.data.enable_cash_flow)
      try {
        const featRes = await api.get(`/plan/condo-features/${value}/`)
        setCondoFeatures(featRes.data.features || [])
      } catch { setCondoFeatures([]) }
      setManagerMode('property')
      navigate('/')
      navigate(0)
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    fetchData()
  }, [filters])

  const handleClearSearch = () => setSearch('')

  return (
      <>
        {verifier ? (
            <Box>
              <Card>
                <Box sx={{ p: '1rem', display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="h5">Contas ({condoData?.count ?? 0})</Typography>

                  <TextField
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Buscar por nome, CNPJ, etc."
                      size="small"
                      sx={{ ml: 'auto', minWidth: 280 }}
                      InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                              <IconButton aria-label="Limpar busca" onClick={handleClearSearch} size="small">
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                        ) : null
                      }}
                      onKeyDown={(e) => {
                        // Evita submit/refresh ao apertar Enter em forms
                        if (e.key === 'Enter') e.preventDefault()
                      }}
                  />
                </Box>

                <Divider light sx={{ mb: '.3rem' }} />
                <CardContent>
                  <TableContainer>
                    {loading ? (
                        <Skeleton variant="rounded" height={300} animation="pulse" />
                    ) : condoData?.count ? (
                        <Table sx={{ minWidth: 650 }} aria-label="simple table">
                          <TableCondoHead />
                          <TableCondoBody
                              condoData={condoData}
                              changeCondo={changeCondo}
                          />
                        </Table>
                    ) : (
                        <NoResultsAnimation />
                    )}
                  </TableContainer>
                </CardContent>
              </Card>

              <PaginationGlobal
                  state={condoData}
                  filters={filters}
                  setFilters={setFilters}
              />
            </Box>
        ) : (
            <Skeleton variant="rounded" height={500} animation="pulse" />
        )}
      </>
  )
}

export default CondoList
